import { Pagination, Stack, Typography } from '@mui/material'
import { isEmpty } from 'lodash'
import React, { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { EmptyStateMessage } from 'components/common/EmptyStateMessage'
import PageSection from 'components/common/PageSection'
import { CompanyDashboardHeader } from 'components/reliability-management/company-dashboard/CompanyDashboardHeader'
import { DashboardListControls } from 'components/reliability-management/company-dashboard/DashboardListControls'
import { DashboardReliabilityTrendChart } from 'components/reliability-management/company-dashboard/DashboardReliabilityTrendChart'
import { DashboardServiceListCard } from 'components/reliability-management/company-dashboard/DashboardServiceListCard'
import { DashboardSummary } from 'components/reliability-management/company-dashboard/DashboardSummary'
import { DashboardTeamGroupListCard } from 'components/reliability-management/company-dashboard/DashboardTeamGroupListCard'
import {
  DASHBOARD_LIST_ITEMS_PER_PAGE,
  DASHBOARD_LIST_SORT_MENU_ITEMS,
  DEFAULT_DASHBOARD_LIST_SORTS,
  SortParam
} from 'components/reliability-management/company-dashboard/rm-company-dashboard-utils'
import { ServiceErrorRetryAlert } from 'components/services/common/ServiceErrorRetryAlert'
import { useGremlinQuery } from 'hooks/use-gremlin-query'
import { usePagination } from 'hooks/use-pagination'
import { useRetryAsync } from 'hooks/use-retry-async'
import { DEFAULT_SORT_CATEGORY } from 'hooks/use-stored-sorts'
import {
  CompanyReliabilityReportGroupedServicesResponse,
  CompanyReliabilityReportServicesResponse,
  CompanyReliabilityReportServicesSummaryResponse,
  ReliabilityReportTrendData,
  ServicesGroup
} from 'types/api/policies/reliability-report'
import { naturalSortBy } from 'utils/list'
import { messageFromResponseError } from 'utils/strings'

const sortOrders = {
  asc: 'ascending',
  desc: 'descending'
}

export const CompanyDashboardContainer: React.FC = () => {
  const [groupBy, setGroupBy] = useState<ServicesGroup>(ServicesGroup.NONE)
  const [sorts, setSorts] = useState<SortParam>(
    DEFAULT_DASHBOARD_LIST_SORTS[groupBy]
  )

  const {
    data: companyReliabilityReportTrend,
    isFetching: isFetchingCompanyReliabilityReportTrend,
    isError: hasErrorCompanyReliabilityReportTrend,
    error: companyReliabilityReportTrendError,
    refetch: fetchCompanyReliabilityReportTrend
  } = useGremlinQuery<ReliabilityReportTrendData>(
    'reports/companies/reliability-management/reliability-report-trend',
    {},
    {
      placeholderData: {} as ReliabilityReportTrendData
    }
  )

  const {
    data: servicesSummaryReport,
    isFetching: isFetchingServicesSummaryReport,
    isError: hasErrorServicesSummaryReport,
    error: servicesSummaryReportError,
    refetch: fetchServicesSummaryReport
  } = useGremlinQuery<CompanyReliabilityReportServicesSummaryResponse>(
    'reports/companies/reliability-management/services-summary',
    {},
    {
      placeholderData: {} as CompanyReliabilityReportServicesSummaryResponse
    }
  )

  const {
    data: servicesReport,
    isFetching: isFetchingServicesReport,
    isError: hasErrorServicesReport,
    error: servicesReportError,
    refetch: fetchServicesReport
  } = useGremlinQuery<CompanyReliabilityReportServicesResponse>(
    'reports/companies/reliability-management/services',
    {},
    {
      placeholderData: {} as CompanyReliabilityReportServicesResponse
    }
  )

  const {
    data: teamGroupReport,
    isFetching: isFetchingTeamGroupReport,
    isError: hasErrorTeamGroupReport,
    error: teamGroupReportError,
    refetch: fetchTeamGroupReport
  } = useGremlinQuery<CompanyReliabilityReportGroupedServicesResponse>(
    'reports/companies/reliability-management/grouped',
    {
      params: {
        groupBy: ServicesGroup.TEAM
      }
    },
    {
      placeholderData: {} as CompanyReliabilityReportGroupedServicesResponse
    }
  )

  const {
    isRetrying: isRetryingCompanyReliabilityReportTrend,
    doRetry: doRetryCompanyReliabilityReportTrend
  } = useRetryAsync(fetchCompanyReliabilityReportTrend)

  const {
    isRetrying: isRetryingServicesSummaryReport,
    doRetry: doRetryServicesSummaryReport
  } = useRetryAsync(fetchServicesSummaryReport)

  const {
    isRetrying: isRetryingServicesReport,
    doRetry: doRetryServicesReport
  } = useRetryAsync(fetchServicesReport)

  const {
    isRetrying: isRetryingTeamGroupReport,
    doRetry: doRetryTeamGroupReport
  } = useRetryAsync(fetchTeamGroupReport)

  /**
   * Sorts list items according to the selected groupBy state
   */
  const sortedDashboardListItems = useMemo(() => {
    if (
      (groupBy === ServicesGroup.NONE && isEmpty(servicesReport)) ||
      (groupBy === ServicesGroup.TEAM && isEmpty(teamGroupReport))
    ) {
      return []
    }

    switch (groupBy) {
      case ServicesGroup.NONE:
        return naturalSortBy(
          servicesReport.services,
          (listItem) => listItem[sorts[DEFAULT_SORT_CATEGORY].field],
          sorts[DEFAULT_SORT_CATEGORY].order
        )
      case ServicesGroup.TEAM:
        return naturalSortBy(
          teamGroupReport.groupedServices,
          (listItem) => listItem[sorts[DEFAULT_SORT_CATEGORY].field],
          sorts[DEFAULT_SORT_CATEGORY].order
        )
      default:
        return []
    }
  }, [groupBy, teamGroupReport, servicesReport, sorts])

  const { pagination } = usePagination(
    sortedDashboardListItems,
    DASHBOARD_LIST_ITEMS_PER_PAGE,
    0,
    false
  )

  const printableServiceListHeader = useMemo(() => {
    if (pagination) {
      const { selectedPage, pageSize, subset } = pagination
      const startIndex = selectedPage * pageSize
      const endIndex = startIndex + subset.length
      const itemType = groupBy === ServicesGroup.NONE ? 'Services' : 'Teams'
      const totalItemCount =
        groupBy === ServicesGroup.NONE
          ? servicesReport?.services?.length
          : teamGroupReport?.groupedServices?.length

      const { field, order } = sorts[DEFAULT_SORT_CATEGORY]

      const sortFieldName = DASHBOARD_LIST_SORT_MENU_ITEMS[groupBy].find(
        ({ value }) => value === field
      ).label

      return `${itemType} ${
        startIndex + 1
      } - ${endIndex} out of ${totalItemCount}, sorted by ${sortFieldName} in ${
        sortOrders[order]
      } order`
    }

    return null
  }, [
    groupBy,
    pagination,
    servicesReport?.services?.length,
    sorts,
    teamGroupReport?.groupedServices?.length
  ])

  const isServiceListEmpty =
    isEmpty(servicesReport) || servicesReport.services?.length === 0

  const isGroupedServicesByTeamListEmpty =
    isEmpty(teamGroupReport) || teamGroupReport.groupedServices?.length === 0

  const isDashboardLoading =
    isFetchingCompanyReliabilityReportTrend ||
    isFetchingServicesSummaryReport ||
    isFetchingServicesReport ||
    isFetchingTeamGroupReport

  const isDashboardListLoading =
    isFetchingServicesReport || isFetchingTeamGroupReport

  return (
    <>
      <CompanyDashboardHeader isDashboardLoading={isDashboardLoading} />
      <Stack direction="column" gap={5} ml={2}>
        <PageSection
          data-test-company-dashboard-trend-chart-section
          errorMessage={messageFromResponseError(
            companyReliabilityReportTrendError,
            'There was a problem '
          )}
          hasError={hasErrorCompanyReliabilityReportTrend}
          isLoading={isFetchingCompanyReliabilityReportTrend}
          renderError={() => (
            <ServiceErrorRetryAlert
              alertText="Company Dashboard reliability trend data could not be loaded"
              isRetrying={isRetryingCompanyReliabilityReportTrend}
              onClick={doRetryCompanyReliabilityReportTrend}
            />
          )}
        >
          {() => (
            <DashboardReliabilityTrendChart
              companyReliabilityReportTrend={companyReliabilityReportTrend}
            />
          )}
        </PageSection>
        <PageSection
          data-test-dashboard-services-summary-section
          errorMessage={messageFromResponseError(
            servicesSummaryReportError,
            'There was a problem '
          )}
          hasError={hasErrorServicesSummaryReport}
          isLoading={isFetchingServicesSummaryReport}
          renderError={() => (
            <ServiceErrorRetryAlert
              alertText="Company Dashboard service summary data could not be loaded"
              isRetrying={isRetryingServicesSummaryReport}
              onClick={doRetryServicesSummaryReport}
            />
          )}
        >
          {() => {
            return (
              servicesSummaryReport?.biggestChangers?.length > 0 && (
                <DashboardSummary report={servicesSummaryReport} />
              )
            )
          }}
        </PageSection>
        <div id="PrintPageBreak" />
        <PageSection
          data-test-dashboard-list-section
          isLoading={isDashboardListLoading}
        >
          {() => (
            <Stack direction="column" gap={5}>
              <Typography
                data-test-dashboard-service-list-title
                id="CompanyDashboardServiceListTitle"
                variant="h6"
              >
                All Services ({servicesReport?.services?.length || 0})
              </Typography>
              <Typography
                css={{ display: 'none' }}
                id="CompanyDashboardReportServiceListTitle"
                variant="h6"
              >
                {printableServiceListHeader}
              </Typography>
              <DashboardListControls
                groupBy={groupBy}
                pagination={pagination}
                setGroupBy={setGroupBy}
                setSorts={setSorts}
                sorts={sorts}
              />
              {groupBy === ServicesGroup.NONE && (
                <PageSection
                  data-test-dashboard-service-list-section
                  errorMessage={messageFromResponseError(
                    servicesReportError,
                    'There was a problem '
                  )}
                  hasError={hasErrorServicesReport}
                  renderError={() => (
                    <ServiceErrorRetryAlert
                      alertText="Company Dashboard service list data could not be loaded"
                      isRetrying={isRetryingServicesReport}
                      onClick={doRetryServicesReport}
                    />
                  )}
                >
                  {() =>
                    isServiceListEmpty ? (
                      <EmptyStateMessage
                        data-test-company-dashboard-empty
                        icon="chartNetwork"
                        title="No Services"
                      >
                        <Link to="/services/new">Define a service</Link> to get
                        started with Reliability Management
                      </EmptyStateMessage>
                    ) : (
                      <Stack
                        data-test-company-dashboard-service-list
                        direction="column"
                        gap={2}
                      >
                        {pagination.subset.map((s) => (
                          <DashboardServiceListCard
                            key={s.serviceId}
                            service={s}
                          />
                        ))}
                      </Stack>
                    )
                  }
                </PageSection>
              )}
              {groupBy === ServicesGroup.TEAM && (
                <PageSection
                  data-test-dashboard-team-group-list-section
                  errorMessage={messageFromResponseError(
                    teamGroupReportError,
                    'There was a problem '
                  )}
                  hasError={hasErrorTeamGroupReport}
                  renderError={() => (
                    <ServiceErrorRetryAlert
                      alertText="Company Dashboard team group list data could not be loaded"
                      isRetrying={isRetryingTeamGroupReport}
                      onClick={doRetryTeamGroupReport}
                    />
                  )}
                >
                  {() =>
                    isGroupedServicesByTeamListEmpty ? (
                      <EmptyStateMessage
                        data-test-company-dashboard-empty
                        icon="chartNetwork"
                        title="No Services"
                      >
                        <Link to="/services/new">Define a service</Link> to get
                        started with Reliability Management
                      </EmptyStateMessage>
                    ) : (
                      <Stack
                        data-test-company-dashboard-team-group-list
                        direction="column"
                        gap={2}
                      >
                        {pagination.subset.map((serviceGroup) => (
                          <DashboardTeamGroupListCard
                            key={serviceGroup.groupDetails.name}
                            serviceGroup={serviceGroup}
                          />
                        ))}
                      </Stack>
                    )
                  }
                </PageSection>
              )}
              {pagination.pageCount > 1 && (
                <Pagination
                  data-test-pagination
                  count={pagination.pageCount}
                  id="CompanyDashboardListSectionPagination"
                  page={pagination.selectedPage + 1}
                  sx={{
                    alignSelf: 'center'
                  }}
                  onChange={(_event, newPage) => {
                    pagination.changePage(newPage - 1)
                  }}
                />
              )}
            </Stack>
          )}
        </PageSection>
      </Stack>
    </>
  )
}
